import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, EVENT_TYPE_PAGE } from '../utils/trackEvent.jsx';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Eye, 
  Plus, 
  Search,
  Filter,
  Settings,
  Shield,
  FolderOpen,
  File,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  ChevronDown,
  UserCheck,
  UserX,
  FileCheck,
  BarChart3,
  Database,
  GraduationCap,
  X,
  BookOpen,
  Presentation,
  Monitor,
  Handshake
} from 'lucide-react';
import s3Service from '../services/s3Service.js';
import PartnerAdminAPI from '../services/partnerAdminAPI.js';
import PartnerOnboardingAPI from '../services/partnerOnboardingAPI.js';
import { StepHelpers, STEP_NAMES, STEP_KEYS } from '../constants/stepConstants.js';
import { ConfigHelpers } from '../config/partnerConfig.js';
import { 
  IslamicFinanceManager,
  SalesTrainingManager,
  SystemTrainingManager,
  OtherResourcesManager
} from '../components/admin/ResourceManager.jsx';
import ResourceLibraryManager from '../components/admin/ResourceLibraryManager.jsx';
import ShariaTemplatesManager from '../components/admin/ShariaTemplatesManager.jsx';
import { RESOURCE_TYPES, getAllResourceTypes, getResourceConfig } from '../config/resourceConfig.js';
import { partnerListPrefix } from '../constants/storagePrefix.js';
import { formatFileSize } from '../utils/fileUtils.js';
import LeadershipDealsSection from '../components/admin/LeadershipDealsSection.jsx';
import PipelineImpactReport from '../components/admin/PipelineImpactReport.jsx';

const PartnerAdmin = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('partners');
  const [activeSubMenu, setActiveSubMenu] = useState('all-partners');
  // Consolidated resource files state
  const [resourceFiles, setResourceFiles] = useState({
    [RESOURCE_TYPES.SHARIA_TEMPLATES]: [],
    [RESOURCE_TYPES.ISLAMIC_FINANCE]: [],
    [RESOURCE_TYPES.SALES_TRAINING]: [],
    [RESOURCE_TYPES.SYSTEM_TRAINING]: [],
    [RESOURCE_TYPES.OTHER_RESOURCES]: [],
    [RESOURCE_TYPES.RESOURCE_LIBRARY]: [],
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerFiles, setPartnerFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(new Set());
  const [uploadingCertification, setUploadingCertification] = useState(new Set()); // Track which step is uploading
  const [updatingStepCompletion, setUpdatingStepCompletion] = useState(new Set()); // Track which step completion is being toggled

  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
    checkAdminAccess();
    loadPartners();
    loadAllResourceFiles();
  }, []);

  useEffect(() => {
    filterPartners();
  }, [searchTerm, partners, activeSubMenu]);

  const checkAdminAccess = () => {
    const userRole = localStorage.getItem('userRole') || '';
    if (userRole !== 'admin') {
      alert('You are not authorized to access this page');
      navigate('/partner-signin');
      return;
    }
  };

  const loadPartners = async () => {
    try {
      setIsLoading(true);
      const api = new PartnerAdminAPI();

      try {
        const response = await api.getAllPartners();

        if (response.status === 'success' && response.data?.onboarding) {
          const transformedPartners = response.data.onboarding.map((partner) => {
            const enabledSteps = ConfigHelpers.getEnabledSteps();

            const completedEnabledSteps = enabledSteps.filter((stepConfig) => {
              const stepKey = stepConfig.key;
              return partner.stepData[stepKey]?.completed === true;
            });

            const completedCount = completedEnabledSteps.length;
            const totalEnabledSteps = enabledSteps.length;

            const currentStepDisplay =
              totalEnabledSteps > 0
                ? completedCount === totalEnabledSteps
                  ? totalEnabledSteps
                  : completedCount + 1
                : null;

            let onboardingStatus = 'pending';
            if (completedCount === totalEnabledSteps && totalEnabledSteps > 0) {
              onboardingStatus = 'completed';
            } else if (completedCount > 0) {
              onboardingStatus = 'in_progress';
            }

            return {
              id: partner.userId,
              name: `${partner.user.firstName} ${partner.user.lastName}`,
              email: partner.user.email,
              status: 'active',
              onboardingStatus,
              joinDate: new Date(partner.createdAt).toLocaleDateString(),
              documents: completedCount,
              phone: partner.user.mobile ? atob(partner.user.mobile) : 'N/A',
              address: 'N/A',
              businessType: 'Test',
              shariaCompliance: partner.stepData.shariyah?.completed ? 'certified' : 'pending',
              currentStep: currentStepDisplay,
              totalSteps: totalEnabledSteps,
              apiCurrentStep: partner.currentStep != null ? partner.currentStep + 1 : null,
              stepData: partner.stepData,
              createdAt: partner.createdAt,
              updatedAt: partner.updatedAt,
            };
          });

          setPartners(transformedPartners);
        } else {
          throw new Error('Invalid API response structure');
        }
      } catch (apiError) {
        console.log('API not available, using mock data:', apiError);
        setPartners(api.getMockPartners());
      }
    } catch (error) {
      console.error('Error loading partners:', error);
      setPartners([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load files for a specific resource type
   */
  const loadResourceFilesByType = async (resourceType) => {
    const { s3Prefix } = getResourceConfig(resourceType);
    try {
      if (!s3Service.isReady()) {
        await s3Service.initialize();
      }

      const files = await s3Service.listFiles(s3Prefix);

      const filesWithUrls = await Promise.all(files.map(async (file) => {
        try {
          const signedUrl = await s3Service.getSignedUrl(file.key);
          return {
            ...file,
            url: signedUrl
          };
        } catch (error) {
          console.error('Error generating signed URL', { fileKey: file.key, error: error.message });
          return { ...file };
        }
      }));

      return filesWithUrls;
    } catch (error) {
      console.error('Error loading resource files:', resourceType, error);
      return [];
    }
  };

  /**
   * Load all resource files on component mount
   */
  const loadAllResourceFiles = async () => {
    const resourceTypes = getAllResourceTypes();
    const loadedFiles = await Promise.all(
      resourceTypes.map(async (type) => ({
        type,
        files: await loadResourceFilesByType(type)
      }))
    );

    const newResourceFiles = { ...resourceFiles };
    loadedFiles.forEach(({ type, files }) => {
      newResourceFiles[type] = files;
    });
    setResourceFiles(newResourceFiles);
  };

  /**
   * Reload files for a specific resource type
   */
  const reloadResourceFiles = async (resourceType) => {
    const files = await loadResourceFilesByType(resourceType);
    setResourceFiles(prev => ({
      ...prev,
      [resourceType]: files
    }));
  };

  const filterPartners = () => {
    let filtered = partners.filter(partner =>
      partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply sub-menu filtering
    if (activeSubMenu === 'active-partners') {
      filtered = filtered.filter(partner => partner.status === 'active');
    } else if (activeSubMenu === 'pending-partners') {
      filtered = filtered.filter(partner => partner.status === 'pending');
    } else if (activeSubMenu === 'inactive-partners') {
      filtered = filtered.filter(partner => partner.status === 'inactive');
    }

    setFilteredPartners(filtered);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { 
        color: 'bg-green-600 text-white border-green-600', 
        icon: CheckCircle 
      },
      pending: { 
        color: 'bg-yellow-500 text-white border-yellow-500', 
        icon: Clock 
      },
      inactive: { 
        color: 'bg-red-600 text-white border-red-600', 
        icon: AlertCircle 
      }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1 border font-medium shadow-sm`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getOnboardingStatusBadge = (status) => {
    const statusConfig = {
      completed: { 
        color: 'bg-emerald-600 text-white border-emerald-600', 
        icon: CheckCircle 
      },
      in_progress: { 
        color: 'bg-blue-600 text-white border-blue-600', 
        icon: Clock 
      },
      pending: { 
        color: 'bg-amber-500 text-white border-amber-500', 
        icon: Clock 
      }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1 border font-medium shadow-sm`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const handlePartnerClick = async (partner) => {
    setSelectedPartner(partner);
    const prefix = partnerListPrefix(partner.id);
    try {
      // Load partner files from S3 using the new listFiles method
      const files = await s3Service.listFiles(prefix);
      const filesWithUrls = await Promise.all(files.map(async (file) => {
        try {
          const signedUrl = await s3Service.getSignedUrl(file.key);
          return {
            ...file,
            url: signedUrl
          };
        } catch (error) {
          console.error('Error generating signed URL for:', file.key, error);
          return { ...file };
        }
      }));

      setPartnerFiles(filesWithUrls);
    } catch (error) {
      console.error('Error loading partner files:', error);
      setPartnerFiles([]);
    }
  };

  const handleResourceUpload = async (file, resourceType, folder = null, fileIndex = null, totalFiles = null) => {
    try {
      setIsUploading(true);
      const progressMsg = totalFiles && fileIndex !== null 
        ? `Uploading file ${fileIndex + 1} of ${totalFiles}: ${file.name}...`
        : `Uploading ${file.name}...`;
      setUploadProgress(progressMsg);
      
      await s3Service.uploadGenericResource(file, resourceType, folder);
      
      const successMsg = totalFiles && fileIndex !== null
        ? `File ${fileIndex + 1} of ${totalFiles} uploaded successfully!`
        : 'File uploaded successfully!';
      setUploadProgress(successMsg);
      
      // Only reload files after the last file is uploaded
      if (!totalFiles || fileIndex === totalFiles - 1) {
        await reloadResourceFiles(resourceType);
        setTimeout(() => setUploadProgress(''), 3000);
      }
    } catch (error) {
      console.error('Error uploading resource file', { resourceType, folder, fileName: file.name, error: error.message });
      setUploadProgress(`Upload failed for ${file.name}. Please try again.`);
      throw error;
    } finally {
      // Only set uploading to false after the last file
      if (!totalFiles || fileIndex === totalFiles - 1) {
        setIsUploading(false);
      }
    }
  };

  const handleDeleteResource = async (fileKey, resourceType = RESOURCE_TYPES.SHARIA_TEMPLATES) => {
    try {
      await s3Service.deleteFile(fileKey);
      // Reload files for the specific resource type
      await reloadResourceFiles(resourceType);
    } catch (error) {
      console.error('Error deleting resource file', { fileKey, resourceType, error: error.message });
      alert('Failed to delete file. Please try again.');
    }
  };

  /**
   * Handle admin uploading certification document for a partner
   * Specifically for Shariyah Certification step
   */
  const handleUploadCertification = async (partnerId, stepKey, file) => {
    if (!selectedPartner || !file) return;

    const uploadKey = `${stepKey}-${partnerId}`;
    setUploadingCertification(prev => new Set(prev).add(uploadKey));

    try {
      // Upload file to S3
      const uploadedFile = await s3Service.uploadFile(file, partnerId, stepKey);
      
      // Get existing step data
      const existingStepData = selectedPartner.stepData?.[stepKey] || {};
      const existingFiles = existingStepData.files || [];
      
      // Update step data with new certification file and mark as completed
      const updatedStepData = {
        [stepKey]: {
          completed: true,
          completedAt: new Date().toISOString(),
          files: [
            ...existingFiles,
            {
              name: uploadedFile.name,
              s3Key: uploadedFile.s3Key,
              size: uploadedFile.size,
              uploadDate: uploadedFile.uploadDate,
              uploadedBy: 'admin', // Mark as uploaded by admin
              isCertification: true // Mark as certification document
            }
          ]
        }
      };

      // Update via API
      const api = new PartnerOnboardingAPI();
      // API step mapping: 1=NDA, 2=Meeting, 3=Partnership, 4=Training, 5=Shariyah, 6=Legal, 7=TechSetup, 8=Launch, 9=BusinessDocumentation
      const stepId = StepHelpers.getApiStepId(stepKey) || 5; // Default to Shariyah (5)
      
      // Determine next step - Shariyah's next step in config is null, but TechSetup (7) is disabled
      // So we keep current step as 5 (Shariyah) since there's no enabled next step
      const nextStepId = stepKey === 'shariyah' ? 5 : stepId + 1;
      
      const response = await api.updateStep(partnerId, stepId, updatedStepData, nextStepId);
      
      if (response.status === 'success') {
        // Also save to partner_files section
        try {
          await api.uploadFile(partnerId, stepId, uploadedFile.name, uploadedFile.s3Key);
        } catch (uploadError) {
          console.warn('Failed to save certification to partner_files:', uploadError);
        }
        
        // Reload partners to refresh data
        await loadPartners();
        
        // Update selected partner if it's the same one
        if (selectedPartner.id === partnerId) {
          const updatedPartner = {
            ...selectedPartner,
            stepData: {
              ...selectedPartner.stepData,
              [stepKey]: updatedStepData[stepKey]
            }
          };
          setSelectedPartner(updatedPartner);
        }
        
        alert('Certification document uploaded successfully!');
      } else {
        throw new Error(response.message || 'Failed to update step');
      }
    } catch (error) {
      console.error('Error uploading certification:', error);
      alert('Failed to upload certification document. Please try again.');
    } finally {
      setUploadingCertification(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadKey);
        return newSet;
      });
    }
  };

  const handleToggleStepCompletion = async (partnerId, stepKey, completed) => {
    if (!selectedPartner || selectedPartner.id !== partnerId) return;

    const updateKey = `${partnerId}-${stepKey}`;
    setUpdatingStepCompletion(prev => new Set(prev).add(updateKey));

    try {
      const stepId = StepHelpers.getApiStepId(stepKey);
      if (!stepId) {
        throw new Error(`Unknown step key: ${stepKey}`);
      }

      const existingStepData = selectedPartner.stepData?.[stepKey] || {};
      const updatedStepData = {
        [stepKey]: {
          ...existingStepData,
          completed: !!completed,
          ...(completed ? { completedAt: new Date().toISOString() } : {})
        }
      };

      const api = new PartnerOnboardingAPI();
      const currentStep = selectedPartner.apiCurrentStep ?? stepId; // Backend step ID for API
      const response = await api.updateStep(partnerId, stepId, updatedStepData, currentStep);

      if (response.status === 'success') {
        await loadPartners();
        if (selectedPartner.id === partnerId) {
          setSelectedPartner(prev => ({
            ...prev,
            stepData: {
              ...prev.stepData,
              [stepKey]: updatedStepData[stepKey]
            }
          }));
        }
      } else {
        throw new Error(response.message || 'Failed to update step');
      }
    } catch (error) {
      console.error('Error toggling step completion:', error);
      alert('Failed to update step. Please try again.');
    } finally {
      setUpdatingStepCompletion(prev => {
        const next = new Set(prev);
        next.delete(updateKey);
        return next;
      });
    }
  };

  const renderContent = () => {
    
    // Partners content
    if (activeSubMenu.includes('partner')) {
      return (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search partners..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Partners Grid */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPartners.map((partner) => (
                <Card
                  key={partner.id}
                  className={`cursor-pointer hover:shadow-lg transition-all duration-200 ${
                    selectedPartner?.id === partner.id ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''
                  }`}
                  onClick={() => handlePartnerClick(partner)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{partner.name}</CardTitle>
                        <p className="text-sm text-gray-600">{partner.email}</p>
                      </div>
                      {getStatusBadge(partner.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Onboarding:</span>
                        {getOnboardingStatusBadge(partner.onboardingStatus)}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Current Step:</span>
                        <span className="text-sm font-medium">
                          {partner.currentStep != null && partner.totalSteps != null
                            ? `Step ${partner.currentStep} of ${partner.totalSteps}`
                            : partner.currentStep != null
                              ? `Step ${partner.currentStep}`
                              : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Completed Steps:</span>
                        <span className="text-sm font-medium">{partner.documents}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Business Type:</span>
                        <span className="text-sm font-medium">{partner.businessType}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Joined:</span>
                        <span className="text-sm font-medium">{partner.joinDate}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Partner Details Modal */}
          {selectedPartner && (
            <Card className="mt-6">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl">{selectedPartner.name}</CardTitle>
                    <p className="text-gray-600">{selectedPartner.email}</p>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(selectedPartner.status)}
                    {getOnboardingStatusBadge(selectedPartner.onboardingStatus)}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPartner(null)}
                      className="text-xs"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Partner Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Partner Information</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Business Type:</span>
                        <span className="font-medium">{selectedPartner.businessType || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{selectedPartner.phone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Address:</span>
                        <span className="font-medium text-right">{selectedPartner.address || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sharia Compliance:</span>
                        <Badge className={selectedPartner.shariaCompliance === 'certified' ? 'bg-green-600 text-white border-green-600 font-medium shadow-sm' : 'bg-amber-500 text-white border-amber-500 font-medium shadow-sm'}>
                          {selectedPartner.shariaCompliance || 'pending'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Step:</span>
                        <span className="font-medium">
                          {selectedPartner.currentStep != null && selectedPartner.totalSteps != null
                            ? `Step ${selectedPartner.currentStep} of ${selectedPartner.totalSteps}`
                            : selectedPartner.currentStep != null
                              ? `Step ${selectedPartner.currentStep}`
                              : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Completed Steps:</span>
                        <span className="font-medium">{selectedPartner.documents}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Joined:</span>
                        <span className="font-medium">{selectedPartner.joinDate}</span>
                      </div>
                    </div>
                  </div>

                  {/* Step Progress */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">Step Progress</h3>
                    <div className="space-y-3">
                      {(() => {
                        // Get enabled steps in correct order from config
                        const enabledSteps = ConfigHelpers.getEnabledSteps();
                        
                        if (!enabledSteps || enabledSteps.length === 0) {
                          return (
                            <div className="text-center py-4 text-gray-500">
                              <p className="text-sm">No steps configured</p>
                            </div>
                          );
                        }
                        
                        return enabledSteps.map((stepConfig) => {
                          const stepKey = stepConfig.key;
                          const stepData = selectedPartner.stepData?.[stepKey] || {};
                          const isCompleted = stepData.completed || false;
                          const fileCount = stepData.files ? stepData.files.length : 0;
                          const updateKey = `${selectedPartner.id}-${stepKey}`;
                          const isUpdating = updatingStepCompletion.has(updateKey);
                          
                          return (
                            <div key={stepKey} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{stepConfig.title}</p>
                                  <p className="text-xs text-gray-600">
                                    {fileCount} file{fileCount !== 1 ? 's' : ''} uploaded
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={isCompleted ? 'bg-green-600 text-white' : 'bg-yellow-500 text-white'}>
                                  {isCompleted ? 'Done' : 'Pending'}
                                </Badge>
                                {isCompleted ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs"
                                    disabled={isUpdating}
                                    onClick={() => handleToggleStepCompletion(selectedPartner.id, stepKey, false)}
                                  >
                                    {isUpdating ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600" />
                                    ) : (
                                      'Mark not completed'
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                    disabled={isUpdating}
                                    onClick={() => handleToggleStepCompletion(selectedPartner.id, stepKey, true)}
                                  >
                                    {isUpdating ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600" />
                                    ) : (
                                      'Mark completed'
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>

                {/* Partner Files by Step - Full Width */}
                <div className="mt-6 space-y-4">
                  <h3 className="font-semibold text-gray-900">Onboarding Steps & Files</h3>
                  <div className="max-h-96 overflow-y-auto space-y-4">
                    {(() => {
                      // Get enabled steps in correct order from config
                      const enabledSteps = ConfigHelpers.getEnabledSteps();
                      
                      if (!enabledSteps || enabledSteps.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500">
                            <File className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <p>No steps configured</p>
                          </div>
                        );
                      }
                      
                      return enabledSteps.map((stepConfig) => {
                        const stepKey = stepConfig.key;
                        const stepData = selectedPartner.stepData?.[stepKey] || {};
                        const isCompleted = stepData.completed || false;
                        
                        const isShariyahStep = stepKey === 'shariyah';
                        const isUploading = uploadingCertification.has(`${stepKey}-${selectedPartner.id}`);
                        const updateKey = `${selectedPartner.id}-${stepKey}`;
                        const isUpdatingStep = updatingStepCompletion.has(updateKey);
                        
                        return (
                          <div key={stepKey} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900">{stepConfig.title}</h4>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={isCompleted ? 'bg-green-600 text-white' : 'bg-yellow-500 text-white'}>
                                  {isCompleted ? 'Completed' : 'Pending'}
                                </Badge>
                                {isCompleted ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs"
                                    disabled={isUpdatingStep}
                                    onClick={() => handleToggleStepCompletion(selectedPartner.id, stepKey, false)}
                                  >
                                    {isUpdatingStep ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600" />
                                    ) : (
                                      'Mark not completed'
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                    disabled={isUpdatingStep}
                                    onClick={() => handleToggleStepCompletion(selectedPartner.id, stepKey, true)}
                                  >
                                    {isUpdatingStep ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600" />
                                    ) : (
                                      'Mark completed'
                                    )}
                                  </Button>
                                )}
                                {/* Admin upload button for Shariyah Certification */}
                                {isShariyahStep && (
                                  <div className="relative">
                                    <input
                                      type="file"
                                      accept=".pdf,.doc,.docx,.rtf,.xls,.xlsx,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file && selectedPartner) {
                                          handleUploadCertification(selectedPartner.id, stepKey, file);
                                        }
                                        // Reset input
                                        e.target.value = '';
                                      }}
                                      disabled={isUploading}
                                      className="hidden"
                                      id={`certification-upload-${stepKey}-${selectedPartner.id}`}
                                    />
                                    <label
                                      htmlFor={`certification-upload-${stepKey}-${selectedPartner.id}`}
                                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded cursor-pointer transition-colors ${
                                        isUploading
                                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                      }`}
                                    >
                                      {isUploading ? (
                                        <>
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                          Uploading...
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="w-3 h-3" />
                                          Upload Certification
                                        </>
                                      )}
                                    </label>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {stepData.files && stepData.files.length > 0 ? (
                              <div className="space-y-2">
                                {stepData.files.map((file, index) => {
                                  const isCertification = file.isCertification || file.uploadedBy === 'admin';
                                  return (
                                    <div key={index} className={`flex items-center gap-3 p-2 rounded-lg ${
                                      isCertification ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50'
                                    }`}>
                                      <div className={`p-1 rounded ${isCertification ? 'bg-emerald-200' : 'bg-gray-200'}`}>
                                        <File className={`w-3 h-3 ${isCertification ? 'text-emerald-600' : 'text-gray-600'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <p className="text-sm font-medium truncate">{file.fileName || file.name}</p>
                                          {isCertification && (
                                            <Badge className="bg-emerald-600 text-white text-xs px-1.5 py-0.5">
                                              Certification
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-600">
                                          {file.size ? formatFileSize(file.size) : 'Unknown size'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : 
                                           file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : 'Unknown date'}
                                          {isCertification && ' • Uploaded by Admin'}
                                        </p>
                                      </div>
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={async () => {
                                            const fileId = `${stepKey}-${index}`;
                                            setLoadingFiles(prev => new Set(prev).add(fileId));
                                            
                                            try {
                                              if (file.s3Url) {
                                                window.open(file.s3Url, '_blank');
                                              } else if (file.s3Key) {
                                                const signedUrl = await s3Service.getSignedUrl(file.s3Key);
                                                window.open(signedUrl, '_blank');
                                              }
                                            } catch (error) {
                                              console.error('Error opening file:', error);
                                              alert('Unable to open file. Please try again.');
                                            } finally {
                                              setLoadingFiles(prev => {
                                                const newSet = new Set(prev);
                                                newSet.delete(fileId);
                                                return newSet;
                                              });
                                            }
                                          }}
                                          disabled={loadingFiles.has(`${stepKey}-${index}`)}
                                          className="text-xs"
                                        >
                                          {loadingFiles.has(`${stepKey}-${index}`) ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                                          ) : (
                                            <Eye className="w-3 h-3" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-gray-500">
                                <File className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">No files uploaded for this step</p>
                                {isShariyahStep && !isCompleted && (
                                  <p className="text-xs text-emerald-600 mt-2">
                                    Admin can upload certification document using the button above
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {isCompleted && stepData.completedAt && (
                              <p className="text-xs text-gray-500 mt-2">
                                Completed: {new Date(stepData.completedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      );
    }

    // Resources content
    if (activeSubMenu === RESOURCE_TYPES.ISLAMIC_FINANCE) {
      return (
        <IslamicFinanceManager
          files={resourceFiles[RESOURCE_TYPES.ISLAMIC_FINANCE]}
          onUpload={handleResourceUpload}
          onDelete={handleDeleteResource}
          uploadProgress={uploadProgress}
        />
      );
    }

    if (activeSubMenu === RESOURCE_TYPES.SALES_TRAINING) {
      return (
        <SalesTrainingManager
          files={resourceFiles[RESOURCE_TYPES.SALES_TRAINING]}
          onUpload={handleResourceUpload}
          onDelete={handleDeleteResource}
          uploadProgress={uploadProgress}
        />
      );
    }

    if (activeSubMenu === RESOURCE_TYPES.SYSTEM_TRAINING) {
      return (
        <SystemTrainingManager
          files={resourceFiles[RESOURCE_TYPES.SYSTEM_TRAINING]}
          onUpload={handleResourceUpload}
          onDelete={handleDeleteResource}
          uploadProgress={uploadProgress}
        />
      );
    }

    if (activeSubMenu === RESOURCE_TYPES.SHARIA_TEMPLATES) {
      return (
        <ShariaTemplatesManager
          files={resourceFiles[RESOURCE_TYPES.SHARIA_TEMPLATES]}
          onUpload={handleResourceUpload}
          onDelete={handleDeleteResource}
          uploadProgress={uploadProgress}
        />
      );
    }

    if (activeSubMenu === RESOURCE_TYPES.OTHER_RESOURCES) {
      return (
        <OtherResourcesManager
          files={resourceFiles[RESOURCE_TYPES.OTHER_RESOURCES]}
          onUpload={handleResourceUpload}
          onDelete={handleDeleteResource}
          uploadProgress={uploadProgress}
        />
      );
    }

    if (activeSubMenu === RESOURCE_TYPES.RESOURCE_LIBRARY) {
      return (
        <ResourceLibraryManager
          files={resourceFiles[RESOURCE_TYPES.RESOURCE_LIBRARY]}
          resourceType={RESOURCE_TYPES.RESOURCE_LIBRARY}
          onUpload={handleResourceUpload}
          onDelete={handleDeleteResource}
          uploadProgress={uploadProgress}
        />
      );
    }

    if (activeSubMenu === 'leadership-deals') {
      return <LeadershipDealsSection />;
    }

    if (activeSubMenu === 'pipeline-impact-report') {
      return <PipelineImpactReport />;
    }

    // Analytics content
    if (activeSubMenu.includes('progress')) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Users className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Partners</p>
                    <p className="text-2xl font-bold text-gray-900">{partners.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <UserCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Partners</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {partners.filter(p => p.status === 'active').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pending Partners</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {partners.filter(p => p.status === 'pending').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileCheck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Resources</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {Object.values(resourceFiles).reduce((sum, files) => sum + files.length, 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    // Default content
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
        <div className="p-4 bg-gray-100 rounded-full mb-4">
          <Database className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Section</h3>
        <p className="text-sm text-gray-600 text-center">
          Choose a section from the left navigation to view content
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Partner Admin</h1>
                <p className="text-gray-600">Manage partners and resources</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/partner-dashboard')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Vertical Navigation */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          {/* Navigation Menu */}
          <div className="p-4">
            <nav className="space-y-2">
              {/* Partners Section */}
              <div>
                <button
                  onClick={() => setActiveTab('partners')}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'partners'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-3" />
                    Partners
                  </div>
                  {activeTab === 'partners' ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {activeTab === 'partners' && (
                  <div className="ml-6 mt-2 space-y-1">
                    <button
                      onClick={() => setActiveSubMenu('all-partners')}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === 'all-partners'
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Users className="w-4 h-4 mr-3" />
                      All Partners ({partners.length})
                    </button>
                    <button
                      onClick={() => setActiveSubMenu('active-partners')}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === 'active-partners'
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <UserCheck className="w-4 h-4 mr-3" />
                      Active Partners
                    </button>
                    <button
                      onClick={() => setActiveSubMenu('pending-partners')}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === 'pending-partners'
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Clock className="w-4 h-4 mr-3" />
                      Pending Partners
                    </button>
                    <button
                      onClick={() => setActiveSubMenu('inactive-partners')}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === 'inactive-partners'
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <UserX className="w-4 h-4 mr-3" />
                      Inactive Partners
                    </button>
                  </div>
                )}
              </div>

              {/* Resources Section */}
              <div>
                <button
                  onClick={() => setActiveTab('resources')}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'resources'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-3" />
                    Resources
                  </div>
                  {activeTab === 'resources' ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {activeTab === 'resources' && (
                  <div className="ml-6 mt-2 space-y-1">
                    <button
                      onClick={() => setActiveSubMenu(RESOURCE_TYPES.ISLAMIC_FINANCE)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === RESOURCE_TYPES.ISLAMIC_FINANCE
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <BookOpen className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">Islamic Financing 101</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex-shrink-0 ml-2">
                        {resourceFiles[RESOURCE_TYPES.ISLAMIC_FINANCE].length}
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveSubMenu(RESOURCE_TYPES.SALES_TRAINING)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === RESOURCE_TYPES.SALES_TRAINING
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <Presentation className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">Sales Training</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex-shrink-0 ml-2">
                        {resourceFiles[RESOURCE_TYPES.SALES_TRAINING].length}
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveSubMenu(RESOURCE_TYPES.SYSTEM_TRAINING)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === RESOURCE_TYPES.SYSTEM_TRAINING
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <Monitor className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">System Training</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex-shrink-0 ml-2">
                        {resourceFiles[RESOURCE_TYPES.SYSTEM_TRAINING].length}
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveSubMenu(RESOURCE_TYPES.SHARIA_TEMPLATES)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === RESOURCE_TYPES.SHARIA_TEMPLATES
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <FileCheck className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">Shariyah Templates</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex-shrink-0 ml-2">
                        {resourceFiles[RESOURCE_TYPES.SHARIA_TEMPLATES].length}
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveSubMenu(RESOURCE_TYPES.OTHER_RESOURCES)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === RESOURCE_TYPES.OTHER_RESOURCES
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <FolderOpen className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">Other Resources</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex-shrink-0 ml-2">
                        {resourceFiles[RESOURCE_TYPES.OTHER_RESOURCES].length}
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveSubMenu(RESOURCE_TYPES.RESOURCE_LIBRARY)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === RESOURCE_TYPES.RESOURCE_LIBRARY
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <BookOpen className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">Resource Library</span>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex-shrink-0 ml-2">
                        {resourceFiles[RESOURCE_TYPES.RESOURCE_LIBRARY].length}
                      </span>
                    </button>
                  </div>
                )}
              </div>

              {/* Analytics Section */}
              <div>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'analytics'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <BarChart3 className="w-4 h-4 mr-3" />
                    Analytics
                  </div>
                  {activeTab === 'analytics' ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {activeTab === 'analytics' && (
                  <div className="ml-6 mt-2 space-y-1">
                    <button
                      onClick={() => setActiveSubMenu('onboarding-progress')}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === 'onboarding-progress'
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4 mr-3" />
                      Onboarding Progress
                    </button>
                    <button
                      onClick={() => setActiveSubMenu('leadership-deals')}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === 'leadership-deals'
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Handshake className="w-4 h-4 mr-3" />
                      Deal pipeline
                    </button>
                    <button
                      onClick={() => setActiveSubMenu('pipeline-impact-report')}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        activeSubMenu === 'pipeline-impact-report'
                          ? 'bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <FileText className="w-4 h-4 mr-3" />
                      Pipeline impact report
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>

        {/* Right Side - Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Content Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {activeSubMenu === 'all-partners' && 'All Partners'}
                  {activeSubMenu === 'active-partners' && 'Active Partners'}
                  {activeSubMenu === 'pending-partners' && 'Pending Partners'}
                  {activeSubMenu === 'inactive-partners' && 'Inactive Partners'}
                  {activeSubMenu === 'islamic-finance-101' && 'Islamic Financing 101'}
                  {activeSubMenu === 'sales-training' && 'Sales Training'}
                  {activeSubMenu === 'system-training' && 'System Training'}
                  {activeSubMenu === 'sharia-templates' && 'Shariyah Templates'}
                  {activeSubMenu === 'other-resources' && 'Other Resources'}
                  {activeSubMenu === 'resource-library' && 'Resource Library (Partners)'}
                  {activeSubMenu === 'onboarding-progress' && 'Onboarding Progress'}
                  {activeSubMenu === 'leadership-deals' && 'Deal pipeline'}
                  {activeSubMenu === 'pipeline-impact-report' && 'Pipeline impact report'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {activeSubMenu.includes('partner') && 'Manage and monitor partner accounts'}
                  {activeSubMenu === 'islamic-finance-101' && 'Core principles of Islamic financing and halal banking practices'}
                  {activeSubMenu === 'sales-training' && 'How to explain halal loan structures to customers'}
                  {activeSubMenu === 'system-training' && 'Technology platform usage and operational procedures'}
                  {activeSubMenu === 'sharia-templates' && 'Upload and manage Sharia compliance templates'}
                  {activeSubMenu === 'other-resources' && 'Additional resources and documentation for partners'}
                  {activeSubMenu === 'resource-library' &&
                    'Store Professional Development source files; wire packages in resourceLibraryPackages.js'}
                  {activeSubMenu.includes('stats') && 'Partner analytics and insights'}
                  {activeSubMenu.includes('progress') && 'Onboarding progress tracking'}
                  {activeSubMenu === 'leadership-deals' &&
                    'Riba-free application flow: demand signals for partners'}
                  {activeSubMenu === 'pipeline-impact-report' &&
                    'Exportable report from captured application intake data'}
                </p>
              </div>
              {/* Upload buttons are now handled by ResourceManager components */}
            </div>
          </div>

          {/* Content Body */}
          <div className="flex-1 p-6 bg-gray-50 overflow-y-auto">
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Upload modals are now handled by ResourceManager components */}

    </div>
  );
};

export default PartnerAdmin;
